import {
  HUD87_CAPABILITY_LINE_FLOOR_VALUE,
  HUD87_CAPABILITY_PAST_LINE_STROKE_WIDTH_PX,
} from "../../../hud/variants"
import type { TimeValuePoint } from "../interpolate"
import type { StripPlotArea } from "../stripLayout"
import { DESKTOP_VIEW_Y_MIN, type StripCamera } from "../camera/useStripCamera"
import {
  buildLinePath,
  buildLineScreenPoints,
  type LineProjection,
  type LineSpan,
} from "./linePaths"

// Stroke of the plain squish line; desktop-mirror mode uses the HUD87
// desktop strokes instead.
export const MOBILE_STRIP_LINE_STROKE_PX = 2
export const MOBILE_STRIP_TIP_DOT_RADIUS_PX = 2.5

// The desktop-mirror future segment, drawn SOLID: desktop's dashed
// round-cap stroke re-rasterizes every animated-date frame and was too
// expensive on mobile, so the dash is intentionally not mirrored.
export const DESKTOP_FUTURE_LINE_COLOR = "#000000"
export const DESKTOP_FUTURE_LINE_STROKE_PX = 2

// Everything the strip draws for the ACTIVE plan's capability line.
export type CapabilityLineArt = {
  pastLinePath: string
  futureLinePath: string | null
  // Past + future control points, for the dither's exclusion tests (the
  // linear segments approximate the drawn curve well at this scale).
  exclusionLinePoints: Array<[number, number]>
  tip: { x: number; y: number }
  // Horizontal bleed window, shared with the choice-point fan paths.
  bleedStartT: number
  bleedEndT: number
}

type CapabilityLineInputs = {
  isDesktopMirror: boolean
  plot: StripPlotArea
  camera: StripCamera
  proj: LineProjection
  linePoints: TimeValuePoint[]
  lineValueAt: (time: number) => number
  currentT: number
}

// squish: single plain line, revealed to the current date. desktop:
// HUD87 styling — solid dark-red past segment to the anchor, black
// future segment to the frame's right edge, values floored like desktop.
export const buildCapabilityLineArt = (inputs: CapabilityLineInputs): CapabilityLineArt => {
  const { isDesktopMirror, camera, currentT, proj } = inputs
  const topClampY = pastLineTopClampY(inputs)
  const pastEndT = pastLineEndT(inputs, topClampY)
  const bleed = bleedWindow(inputs)
  const pastSpan = isDesktopMirror
    ? activeLineSpan(inputs, bleed.startT, pastEndT, HUD87_CAPABILITY_LINE_FLOOR_VALUE, topClampY)
    : activeLineSpan(inputs, camera.viewStartT, currentT, 0)
  // The future segment keeps exiting the top unpinned — the pre-choice
  // "projection shoots into the red" beat is designed clipping. (Past
  // the ceiling crossing its values are above the frame anyway, so the
  // gap between pastEndT and anchorT never shows.)
  const futureSpan = isDesktopMirror
    ? activeLineSpan(inputs, camera.anchorT, bleed.endT, HUD87_CAPABILITY_LINE_FLOOR_VALUE)
    : null
  return {
    pastLinePath: buildLinePath(proj, pastSpan),
    futureLinePath: futureSpan === null ? null : buildLinePath(proj, futureSpan),
    exclusionLinePoints: futureSpan === null
      ? buildLineScreenPoints(proj, pastSpan)
      : [
          ...buildLineScreenPoints(proj, pastSpan),
          ...buildLineScreenPoints(proj, futureSpan),
        ],
    tip: tipPosition(inputs, pastEndT, topClampY),
    bleedStartT: bleed.startT,
    bleedEndT: bleed.endT,
  }
}

const activeLineSpan = (
  inputs: CapabilityLineInputs,
  fromT: number,
  toT: number,
  floorValue: number,
  topClampY: number | null = null,
): LineSpan => ({
  fromT,
  toT,
  floorValue,
  topClampY,
  series: inputs.linePoints,
  valueAt: inputs.lineValueAt,
})

// Frame-freezing plan modes pin the PAST line to the frame's top edge so
// the ending shot keeps the red line ACROSS the screen; null = no pin
// (production: the line is designed to outgrow the frame and exit; the
// svg clips it).
const pastLineTopClampY = ({ isDesktopMirror, camera, plot }: CapabilityLineInputs): number | null =>
  isDesktopMirror
  && (camera.planFrameMode === "freeze" || camera.planFrameMode === "flop-freeze")
    ? plot.padding.top + HUD87_CAPABILITY_PAST_LINE_STROKE_WIDTH_PX / 2 + 1
    : null

// The pinned line STOPS where it meets the ceiling — no flat tail
// running along the top and off the right edge; the ending shot is the
// line rising across the frame and ending at the top, dot at the bend.
const pastLineEndT = (inputs: CapabilityLineInputs, topClampY: number | null): number => {
  const crossT = ceilingCrossT(inputs, topClampY)
  return crossT === null ? inputs.camera.anchorT : Math.min(inputs.camera.anchorT, crossT)
}

// Linear-interpolated first crossing of the pinned ceiling's value;
// null = the line never reaches it (or there is no pin).
const ceilingCrossT = (
  { camera, plot, linePoints }: CapabilityLineInputs,
  topClampY: number | null,
): number | null => {
  if (topClampY === null || linePoints.length === 0) return null
  const ySpan = camera.frameYMax - DESKTOP_VIEW_Y_MIN || 1
  const ceilingValue = DESKTOP_VIEW_Y_MIN
    + ((plot.padding.top + plot.innerHeight - topClampY) / plot.innerHeight) * ySpan
  return firstTimeAtOrAbove(linePoints, ceilingValue)
}

const firstTimeAtOrAbove = (points: TimeValuePoint[], threshold: number): number | null => {
  let prev = points[0]
  if (prev.value >= threshold) return prev.time
  for (const point of points) {
    if (point.value >= threshold) return crossingTime(prev, point, threshold)
    prev = point
  }
  return null
}

const crossingTime = (a: TimeValuePoint, b: TimeValuePoint, threshold: number): number => {
  const ratio = b.value === a.value ? 1 : (threshold - a.value) / (b.value - a.value)
  return a.time + ratio * (b.time - a.time)
}

// The line bleeds to the card edges like the band: extrapolate the time
// axis past the plot padding on both sides (the series clamps flat at
// its ends, so the tails just continue the curve).
const bleedWindow = ({ isDesktopMirror, camera, plot, currentT }: CapabilityLineInputs) => ({
  startT: isDesktopMirror
    ? camera.viewStartT - (plot.padding.left / plot.innerWidth) * camera.viewSpan
    : camera.viewStartT,
  endT: isDesktopMirror
    ? camera.viewEndT + (plot.padding.right / plot.innerWidth) * camera.viewSpan
    : currentT,
})

// The endpoint dot rides the line and parks at the ceiling bend once the
// foom passes it. Same render-bounds cap as the line: teaser autoplays
// leave the raw tip millions of px above the strip.
const tipPosition = (
  inputs: CapabilityLineInputs,
  pastEndT: number,
  topClampY: number | null,
): { x: number; y: number } => {
  const { isDesktopMirror, camera, proj } = inputs
  const tipT = isDesktopMirror ? pastEndT : camera.anchorT
  const rawY = proj.yForValue(Math.max(
    inputs.lineValueAt(tipT),
    isDesktopMirror ? HUD87_CAPABILITY_LINE_FLOOR_VALUE : 0,
  ))
  return {
    x: proj.capX(proj.xForTime(tipT)),
    y: proj.capY(topClampY !== null ? Math.max(rawY, topClampY) : rawY),
  }
}
